/**
 * A slice of the prototype's Malaysia manufacturing journey (prototype/js/config.js), used as test
 * fixtures. Companies and people in the prototype are fictional.
 */
export const KINTA_QUESTIONS = {
    Q012: {
        type: 'employee_count',
        text: 'How many people work for the business, including part-time?',
        mandatory: true,
        metadata: { risk_domain: 'GV' },
    },
    Q021: {
        type: 'entity_selector',
        text: 'Which email system does the business use?',
        mandatory: true,
        settings: { entity_type: 'technology' },
        options: [
            {
                value: 'microsoft_365',
                label: 'Microsoft 365',
                effects: [{ type: 'start_workflow', params: { workflow: 'WF-M365' } }],
            },
            { value: 'google_workspace', label: 'Google Workspace' },
            { value: 'other', label: 'Other / hosted' },
        ],
    },
    Q031: {
        type: 'boolean',
        text: 'Are any machines, controllers or production lines connected to your network?',
        mandatory: true,
        visibility: { fact: 'industry', op: 'in', value: ['manufacturing', 'logistics'] },
        settings: { allow_not_sure: true },
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'not_sure', label: 'Not sure', effects: [{ type: 'require_human_review' }] },
        ],
        metadata: { risk_domain: 'NS', controls: ['NS-04'] },
    },
    Q044: {
        type: 'single_choice',
        text: 'How do equipment vendors reach your machines for support?',
        mandatory: true,
        visibility: { question: 'Q031', op: 'eq', value: 'yes' },
        depends_on: ['Q031'],
        options: [
            { value: 'on_site', label: 'They come on site only' },
            { value: 'on_request', label: 'On request, we open access' },
            {
                value: 'always_on',
                label: 'Always-on connection',
                effects: [{ type: 'create_risk', params: { risk: 'OT_REMOTE_COMPROMISE' } }],
            },
            { value: 'not_sure', label: 'Not sure' },
        ],
        metadata: { risk_domain: 'TP', controls: ['TP-02'], kill_switch: true },
    },
    Q058: {
        type: 'single_choice',
        text: 'Does everyone need a second step (like a phone prompt) to sign in to email?',
        help: 'This is multi-factor authentication (MFA).',
        mandatory: true,
        options: [
            { value: 'everyone', label: 'Yes, everyone' },
            { value: 'some', label: 'Some people' },
            { value: 'no', label: 'No' },
        ],
        metadata: { risk_domain: 'IA', controls: ['IA-02'], kill_switch: true },
    },
    Q104: {
        type: 'boolean',
        text: 'Is the business certified to ISO 27001?',
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
        ],
    },
    Q105: {
        type: 'single_choice',
        text: 'Which version of the standard?',
        visibility: { question: 'Q104', op: 'eq', value: 'yes' },
        depends_on: ['Q104'],
        options: [
            { value: 'iso27001_2022', label: 'ISO 27001:2022' },
            { value: 'iso27001_2013', label: 'ISO 27001:2013' },
        ],
    },
    Q106: {
        type: 'date',
        text: 'When does the certificate expire?',
        depends_on: ['Q105'],
        effects: [{ type: 'create_reminder', params: { offsets: ['T-120', 'T-90', 'T-60', 'T-30', 'T-7'] } }],
    },
    Q283: {
        type: 'single_choice',
        text: 'When did you last restore files from a backup to check it works?',
        mandatory: true,
        options: [
            { value: 'last_90_days', label: 'In the last 90 days' },
            { value: '3_12_months', label: '3–12 months ago' },
            { value: 'never', label: 'Never / not sure' },
        ],
        metadata: { risk_domain: 'BR', controls: ['BR-03'] },
    },
    Q301: {
        type: 'currency',
        text: 'Roughly what was last year’s revenue?',
        mandatory: true,
        // Up to RM100 billion: brokers place groups far larger than an SME (task 88).
        settings: { currency: 'MYR', max: 100_000_000_000 },
    },
    Q330: {
        type: 'asset_selector',
        text: 'Which systems would stop production or sales if they were down for a day?',
        mandatory: true,
        settings: { min_selected: 1 },
        options: [
            { value: 'erp', label: 'ERP (SAP Business One)' },
            { value: 'email', label: 'Email' },
            { value: 'cnc', label: 'CNC / CAM workstations' },
        ],
    },
    Q402: {
        type: 'file_upload',
        text: 'Upload your current insurance policy schedule (if you have one).',
        settings: { max_files: 2, document_types: ['insurance_schedule'] },
    },
};
/** The prototype's stages (S01–S08), holding the questions above. */
export const KINTA_STAGES = {
    S01: {
        name: 'Know your business',
        description: 'Who you are, how big, what you sell and where.',
        questions: ['Q012', 'Q031', 'Q104', 'Q105', 'Q106', 'Q301'],
        estimated_minutes: 3,
        completion_message: 'Nice. We now know enough to tailor the rest of the check.',
    },
    S02: {
        name: 'Digital footprint',
        mode: 'parallel',
        questions: ['Q021'],
        estimated_minutes: 2,
    },
    S03: { name: 'Data', mode: 'parallel', questions: ['Q330'], estimated_minutes: 3 },
    S04: {
        name: 'Identity',
        questions: ['Q058'],
        estimated_minutes: 4,
        completion_message: 'Identity is where most SME breaches start. You are ahead of most.',
    },
    S05: { name: 'Ransomware', questions: ['Q283'], estimated_minutes: 4 },
    S06: {
        name: 'OT security',
        mode: 'conditional',
        entry: { question: 'Q031', op: 'eq', value: 'yes' },
        mandatory: false,
        questions: ['Q044'],
        estimated_minutes: 5,
    },
    S07: { name: 'Recovery', estimated_minutes: 3 },
    S08: { name: 'Insurance readiness', questions: ['Q402'], estimated_minutes: 2 },
};
/** SME Cyber Assessment · Manufacturing (MY): S02 and S03 run in parallel; S05 opens once S04 is started. */
export const KINTA_JOURNEY = {
    name: 'SME Cyber Assessment · Manufacturing (MY)',
    stages: [
        { stage: 'S01' },
        { stage: 'S02', after: ['S01'] },
        { stage: 'S03', after: ['S01'] },
        { stage: 'S04', after: ['S02', 'S03'] },
        { stage: 'S05', after: [{ stage: 'S04', when: 'started' }] },
        { stage: 'S06', after: ['S05'] },
        { stage: 'S07', after: ['S05'] },
        { stage: 'S08', after: ['S04', 'S05', 'S07'] },
    ],
};
//# sourceMappingURL=kinta.js.map